import { useEffect, useLayoutEffect, useState } from 'react';
import {
	useOverlapsQuickstart,
	useOverlapsImportFlag,
} from '@atlassian/jira-business-onboarding-common';
import { getFlags, setFlag } from '@atlassian/jira-common-have-i-seen-it';
import log from '@atlassian/jira-common-util-logging/src/log';
import {
	QUICKSTART_UI_STATUS,
	useQuickstartUiStatus,
} from '@atlassian/jira-onboarding-quickstart-core/src/services';
import type { ColumnId } from '@atlassian/jira-platform-board-kit';

export interface Column {
	id: ColumnId;
	statusCategory?: string;
	statusCategoryId?: number;
}

export const AUTOMATION_DISCOVERY_FLAG_KEY = 'jira.automation.discovery.on.board.columns';
export const useAutomationDiscoverySpotlight = (columns: Column[]) => {
	const [spotlightDismissed, setSpotlightDismissed] = useState(true);
	const [hasSeenFlag, setHasSeenFlag] = useState(false);
	const [spotlightTargetHasRendered, setSpotlightTargetHasRendered] = useState(false);

	const { overlapsWithQuickstart } = useOverlapsQuickstart();
	const { overlapsWithImportFlag } = useOverlapsImportFlag();
	const [quickstartStatus] = useQuickstartUiStatus();

	useLayoutEffect(() => {
		if (
			!overlapsWithImportFlag &&
			quickstartStatus !== QUICKSTART_UI_STATUS.NOT_LOADED &&
			!overlapsWithQuickstart
		) {
			setSpotlightDismissed(false);
		}
	}, [overlapsWithImportFlag, overlapsWithQuickstart, quickstartStatus]);

	useEffect(() => {
		const fetchFlagFn = async () =>
			getFlags({ [`${AUTOMATION_DISCOVERY_FLAG_KEY}`]: {} })
				.then((response) => {
					if (response[`${AUTOMATION_DISCOVERY_FLAG_KEY}`]?.status) {
						setHasSeenFlag(true);
					}
				})
				.catch(() => {
					log.safeErrorWithoutCustomerData(
						'automation-discovery',
						'Failed to fetch have-i-seen-it flags.',
					);
				});

		fetchFlagFn();
	}, []);

	const targetColumnId =
		columns.find(
			(column) => column.statusCategory === 'indeterminate' || column.statusCategoryId === 4,
		)?.id ?? '';

	const isTargetColumn = (columnId: number) => {
		if (Number(columnId) === 0 || Number(targetColumnId) === 0) {
			return false;
		}
		return Number(columnId) === Number(targetColumnId);
	};

	const shouldShowSpotlight = (columnId: number) =>
		spotlightTargetHasRendered && !hasSeenFlag && !spotlightDismissed && isTargetColumn(columnId);

	const dismissSpotlight = () => {
		setFlag(`${AUTOMATION_DISCOVERY_FLAG_KEY}`);
		setSpotlightDismissed(true);
	};

	const setSpotlightTargetHasRenderedState = () => {
		if (!spotlightTargetHasRendered) {
			setSpotlightTargetHasRendered(true);
		}
	};

	return {
		targetColumnId,
		isTargetColumn,
		shouldShowSpotlight,
		dismissSpotlight,
		setSpotlightTargetHasRendered: setSpotlightTargetHasRenderedState,
		spotlightTargetHasRendered,
	};
};
